import React from 'react'
import Image, { ImageSrc } from 'src/components/Image'
import useBodyScroll from 'src/hooks/useBodyScroll'
import { css } from 'src/style/css'
import { Theme } from 'src/style/Theme'
import { getWindow } from 'src/util/getWindow'
import { useTheme } from '@emotion/react'
import { useT } from 'src/i18n/useT'
import { msg } from 'src/i18n/translation/MsgKeys'
import { Parallax } from 'src/components/Parallax'
import { ClutchImageWidget } from 'src/components/ClutchWidget'

const calcTextOpacity = (): number => {
    const bodyScroll = useBodyScroll()
    const { innerHeight: viewHeight } = getWindow({ innerHeight: 1 })
    // Calculated variable which determines number that multiplies height ratio, because of different viewport heights
    const viewHeightRatio = 1.8 / (viewHeight / bodyScroll.documentHeight)
    return 1 - bodyScroll.heightRatio * viewHeightRatio
}

const HeroText = ({ title, subtitle }: { title: string; subtitle: string }) => {
    const textOpacity = calcTextOpacity()
    return (
        <div
            css={(th: Theme) => css`
                display: flex;
                flex-direction: column;
                align-items: center;
                box-sizing: border-box;
                transform: translate(0, 10em);
                padding: 1.35rem 2.4rem 2rem;
                background-color: rgba(0, 0, 0, 0.6);
                @media (max-width: ${th.mediaQuery.xl}) {
                    transform: translate(0, 6em);
                    padding: 1.2rem 2rem 1.7rem;
                }
                @media (max-width: ${th.mediaQuery.lg}) {
                    width: min(86vw, 760px);
                    padding: 1rem 1.6rem 1.45rem;
                }
                @media (max-width: ${th.mediaQuery
                        .lg}) and (max-height: 700px) {
                    transform: translate(0, 2em);
                }
                // Remove y offset on smaller screens
                @media (max-width: ${th.mediaQuery.md}) {
                    transform: translate(0, 0%);
                    width: min(88vw, 640px);
                    padding: 0.9rem 1.25rem 1.25rem;
                }
                @media (max-width: 599px) {
                    padding: 0 1rem 1rem 1rem;
                    background-color: transparent;
                }
                @media (max-width: ${th.mediaQuery.sm}) {
                    padding: 0 1rem 1rem 1rem;
                    background-color: transparent;
                }
                @media (max-width: ${th.mediaQuery.xs}) {
                    padding: 0 0.9rem 0.85rem 0.9rem;
                    background-color: transparent;
                }
                > *:not(:last-child) {
                    margin-bottom: ${th.spacing.md};
                }
                opacity: ${textOpacity};
                color: ${th.colors.white};
                font-size: ${th.fontSize.md};
                font-weight: ${th.fontWeight.thin};
                text-align: center;
            `}
        >
            <h1
                css={(th: Theme) => css`
                    margin: 0;
                    font-size: 4rem;
                    font-weight: ${th.fontWeight.bold};
                    line-height: 1.1;
                    text-transform: uppercase;
                    @media (max-width: ${th.mediaQuery.xl}) {
                        font-size: 3.5rem;
                    }
                    @media (max-width: ${th.mediaQuery.lg}) {
                        font-size: 3rem;
                    }
                    @media (max-width: ${th.mediaQuery.md}) {
                        font-size: 3rem;
                    }
                    @media (max-width: ${th.mediaQuery.sm}) {
                        font-size: 2rem;
                    }
                `}
            >
                {title}
            </h1>
            <span
                css={(th: Theme) => css`
                    display: block;
                    font-size: ${th.fontSize.lg};
                    line-height: 1.35;
                    @media (max-width: ${th.mediaQuery.lg}) {
                        font-size: ${th.fontSize.md};
                    }
                `}
            >
                {subtitle}
            </span>
        </div>
    )
}

const heroResolutionHeights = (th: Theme) => css`
    @media (max-width: ${th.mediaQuery.lg}) {
        height: 900px;
    }
    @media (max-width: ${th.mediaQuery.md}) {
        height: 700px;
    }
    @media (max-width: ${th.mediaQuery.sm}) {
        height: 600px;
    }
    @media (max-width: ${th.mediaQuery.xs}) {
        height: 500px;
    }
    @media (min-width: ${th.mediaQuery.md}) and (max-width: ${th.mediaQuery
            .lg}) and (max-height: 700px) {
        height: 100vh;
    }
`

type HeroProps = {
    desktopImg: ImageSrc
    mobileImg: ImageSrc
}

// const Video = () => {
//     return (
//         <video
//             css={css`
//                 position: absolute;
//                 top: 0;
//                 left: 0;
//                 width: 100%;
//                 height: 100%;
//                 object-fit: cover;
//             `}
//             autoPlay
//             muted
//             id="myVideo"
//             preload="auto"
//             loop={true}
//             // TODO later update
//             poster="https://v3.cdnpk.net/videvo_files/video/free/2022-01/thumbnails/220114_01_Drone_4k_011_small.jpg"
//         >
//             <source
//                 src="https://v3.cdnpk.net/videvo_files/video/free/2022-01/large_watermarked/220114_01_Drone_4k_011_preview.mp4"
//                 type="video/mp4"
//             />
//             {"Your browser doesn't support this video format."}
//         </video>
//     )
// }

const Hero = ({ desktopImg, mobileImg }: HeroProps) => {
    const th = useTheme() as Theme
    const t = useT()
    return (
        <div
            css={(theme: Theme) => css`
                position: sticky;
                top: 0;
                overflow: hidden;
                height: 100vh;
                z-index: 0;
                ${heroResolutionHeights(theme)};
                background: ${theme.colors.white};
            `}
        >
            <ClutchImageWidget />
            <div
                css={(theme: Theme) => css`
                    display: block;
                    width: 100%;
                    height: 100%;
                    @media (max-width: ${theme.mediaQuery.lg}) {
                        display: none;
                    }
                `}
            >
                <Image
                    src={desktopImg}
                    alt="Garwan"
                    loading="eager"
                    css={css`
                        width: 100%;
                        height: 100%;
                        &,
                        & > div {
                            height: 100%;
                        }
                        img {
                            object-fit: cover !important;
                            object-position: center top !important;
                            filter: brightness(80%);
                        }
                    `}
                    backgroundColor={th.colors.white}
                />
            </div>
            <div
                css={(theme: Theme) => css`
                    display: none;
                    height: 100%;
                    @media (max-width: ${theme.mediaQuery.lg}) {
                        display: block;
                    }
                `}
            >
                <div
                    css={css`
                        width: 100%;
                        height: 100%;
                    `}
                >
                    {/* <Video /> */}
                    <Image
                        src={mobileImg}
                        alt="Garwan"
                        loading="eager"
                        css={css`
                            width: 100%;
                            height: 100%;
                            &,
                            & > div {
                                height: 100%;
                            }
                            img {
                                object-fit: cover !important;
                                object-position: bottom !important;
                                filter: brightness(80%);
                            }
                        `}
                        backgroundColor={th.colors.white}
                    />
                </div>
            </div>
            <Parallax
                y={[-15, 15]}
                styleInner={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
                // styles outer
                css={css`
                    position: absolute;
                    width: 100%;
                    top: 0;
                    left: 0;
                `}
            >
                <HeroText
                    title={t(msg.homePage.hero.title)}
                    subtitle={t(msg.homePage.hero.subTitle)}
                />
            </Parallax>
        </div>
    )
}

export default Hero
